import Axios, { AxiosResponse } from 'axios';
import { useAtom, useSetAtom } from "jotai";
import { useRef } from "react";
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks";
import { useLogin } from "../login";
import {
    ConfirmOrderPayload,
    ConfirmOrderResult,
    InteriorProject,
    InteriorProjectDetail,
    InteriorProjectListResponse,
    InteriorProjectUpdatePayload,
    InteriorWall,
    InteriorWallPayload,
    OrderChargePreview,
    ProjectDesignConflict,
    projectDetailAtom,
    projectDetailLoadingAtom,
    projectsAtom,
    projectsLoadingAtom,
    projectsTotalAtom,
    SaveProjectDesignsFailure
} from "./types";

export const INTERIOR_PROJECTS_PAGE_SIZE = 20;

// Discriminated failure returned by confirmProject so the modal can render a
// per-wall readiness breakdown (400 trace.walls[]) instead of a generic error.
export interface ConfirmOrderFailure {
    error: true;
    message: string;
    wallIssues?: { wall_id: number; wall_name: string; issues: string[] }[];
}

// Result of sharing a project: the raw magic-link URL (safe to show the
// authenticated designer) plus the new SHARED status/expiry.
export interface ShareProjectResult {
    share_url: string;
    token_expires_at: string;
    status: string;
    client_email: string;
}

// Allow-lists the wall editor uses to constrain material inputs to exactly what
// the native order flow accepts (mirrors the backend constants). scale_sizes is
// still returned by the endpoint but no longer used by the wall form.
export interface OrderableOptions {
    scale_sizes: string[];
    orderable_material_ids: number[];
}

// #region fileToBase64
// FileReader → data URL (the backend strips the data-URL prefix).
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
};

type InteriorProjectFunctions = {
    listProjects: (page: number, search: string) => Promise<void>;
    createProject: (client_name: string) => Promise<InteriorProject | null>;
    getProjectDetail: (projectId: number) => Promise<InteriorProjectDetail | null>;
    updateProject: (projectId: number, payload: InteriorProjectUpdatePayload) => Promise<boolean>;
    deleteProject: (projectId: number) => Promise<boolean>;
    createWall: (projectId: number, payload: InteriorWallPayload) => Promise<InteriorWall | null>;
    updateWall: (projectId: number, wallId: number, payload: InteriorWallPayload) => Promise<InteriorWall | null>;
    deleteWall: (projectId: number, wallId: number) => Promise<boolean>;
    uploadWallPhoto: (projectId: number, wallId: number, base64Image: string) => Promise<InteriorWall | null>;
    removeWallPhoto: (projectId: number, wallId: number) => Promise<boolean>;
    replaceProjectDesigns: (projectId: number, design_ids: number[]) => Promise<true | SaveProjectDesignsFailure>;
    replaceRecommendedDesigns: (projectId: number, wallId: number, design_ids: number[]) => Promise<boolean>;
    regenerateMockup: (projectId: number, wallId: number, recommendedDesignId: number) => Promise<boolean>;
    confirmProject: (projectId: number, payload: ConfirmOrderPayload) => Promise<ConfirmOrderResult | ConfirmOrderFailure>;
    shareProject: (projectId: number, client_email: string) => Promise<ShareProjectResult | null>;
    getOrderableOptions: () => Promise<OrderableOptions | null>;
    getOrderPreview: (projectId: number) => Promise<OrderChargePreview | null>;
};

type UseInteriorProjects = [
    InteriorProject[],
    InteriorProjectDetail | null,
    InteriorProjectFunctions
];

// #region useInteriorProjects
export const useInteriorProjects = (): UseInteriorProjects => {
    const [projects, setProjects] = useAtom(projectsAtom);
    const [projectDetail, setProjectDetail] = useAtom(projectDetailAtom);
    const setProjectsTotal = useSetAtom(projectsTotalAtom);
    const setProjectsLoading = useSetAtom(projectsLoadingAtom);
    const setProjectDetailLoading = useSetAtom(projectDetailLoadingAtom);

    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    // Sequence guards so stale debounced responses can't clobber newer results
    const listSeqRef = useRef(0);
    const detailSeqRef = useRef(0);

    const headers = { 'X-Wallmates-Auth': authToken };

    const showError = (text: string) => {
        setSnackbar({ show: true, snackbarLevel: 'error', text });
    };

    // #region listProjects
    const listProjects = async (page: number, search: string): Promise<void> => {
        const seq = ++listSeqRef.current;
        setProjectsLoading(true);

        const params = new URLSearchParams({
            page: String(page),
            limit: String(INTERIOR_PROJECTS_PAGE_SIZE)
        });
        if (search.trim().length > 0) params.set('search', search.trim());

        const response: AxiosResponse<InteriorProjectListResponse> | void = await Axios.get(
            `${environment.interiorProjectsUrl}?${params.toString()}`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (seq !== listSeqRef.current) return; // stale response — a newer request is in flight

        if (response) {
            setProjects(response.data.projects);
            setProjectsTotal(response.data.total);
        } else {
            showError('There was an error while loading your projects. Please try again later.');
        }
        setProjectsLoading(false);
    };

    // #region createProject
    const createProject = async (client_name: string): Promise<InteriorProject | null> => {
        try {
            const response: AxiosResponse<InteriorProject> = await Axios.post(
                environment.interiorProjectsUrl,
                { client_name },
                { headers }
            );
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            showError('There was an error while creating the project. Please try again later.');
            return null;
        }
    };

    // #region getProjectDetail
    const getProjectDetail = async (projectId: number): Promise<InteriorProjectDetail | null> => {
        const seq = ++detailSeqRef.current;
        setProjectDetailLoading(true);

        const response: AxiosResponse<InteriorProjectDetail> | void = await Axios.get(
            `${environment.interiorProjectsUrl}/${projectId}`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        // Stale — a newer fetch is in flight and owns the atom/loading state.
        // Return this response's own data so callers never mistake a
        // superseded-but-successful fetch for a 404.
        if (seq !== detailSeqRef.current) return response ? response.data : projectDetail;

        setProjectDetailLoading(false);
        if (response) {
            setProjectDetail(response.data);
            return response.data;
        }
        setProjectDetail(null);
        return null;
    };

    // Re-fetch the detail aggregate after any wall-level mutation so signed
    // URLs, ranks and completeness state stay in sync with the server.
    const refreshDetail = async (projectId: number): Promise<void> => {
        await getProjectDetail(projectId);
    };

    // Shared error handling for mutations: 409 = project ORDERED (read-only),
    // 404 = project/wall gone (other tab / other brand) — refresh to resync.
    const handleMutationError = (err: any, projectId: number, action: string): void => {
        const status = err?.response?.status;
        if (status === 409) {
            showError('This project has been ordered and can no longer be edited.');
            refreshDetail(projectId);
            return;
        }
        if (status === 404) {
            showError('That item no longer exists. Refreshing the project.');
            refreshDetail(projectId);
            return;
        }
        errorCatcher(err, loginFunctions.logout);
        showError(`There was an error while ${action}. Please try again later.`);
    };

    // #region updateProject
    const updateProject = async (projectId: number, payload: InteriorProjectUpdatePayload): Promise<boolean> => {
        try {
            const response: AxiosResponse<InteriorProject> = await Axios.put(
                `${environment.interiorProjectsUrl}/${projectId}`,
                payload,
                { headers }
            );
            setProjectDetail((previous: InteriorProjectDetail | null) =>
                previous && previous.id === projectId ? { ...previous, ...response.data } : previous
            );
            setProjects((previous: InteriorProject[]) =>
                previous.map((p) => p.id === projectId ? { ...p, ...response.data } : p)
            );
            return true;
        } catch (err: any) {
            handleMutationError(err, projectId, 'updating the project');
            return false;
        }
    };

    // #region deleteProject
    const deleteProject = async (projectId: number): Promise<boolean> => {
        try {
            await Axios.delete(`${environment.interiorProjectsUrl}/${projectId}`, { headers });
            setProjects((previous: InteriorProject[]) => previous.filter((p) => p.id !== projectId));
            setProjectsTotal((previous: number) => Math.max(0, previous - 1));
            setProjectDetail((previous: InteriorProjectDetail | null) =>
                previous && previous.id === projectId ? null : previous
            );
            return true;
        } catch (err: any) {
            handleMutationError(err, projectId, 'deleting the project');
            return false;
        }
    };

    // #region createWall
    const createWall = async (projectId: number, payload: InteriorWallPayload): Promise<InteriorWall | null> => {
        try {
            const response: AxiosResponse<InteriorWall> = await Axios.post(
                `${environment.interiorProjectsUrl}/${projectId}/walls`,
                payload,
                { headers }
            );
            await refreshDetail(projectId);
            return response.data;
        } catch (err: any) {
            handleMutationError(err, projectId, 'creating the wall');
            return null;
        }
    };

    // #region updateWall
    const updateWall = async (projectId: number, wallId: number, payload: InteriorWallPayload): Promise<InteriorWall | null> => {
        try {
            const response: AxiosResponse<InteriorWall> = await Axios.put(
                `${environment.interiorProjectsUrl}/${projectId}/walls/${wallId}`,
                payload,
                { headers }
            );
            await refreshDetail(projectId);
            return response.data;
        } catch (err: any) {
            handleMutationError(err, projectId, 'updating the wall');
            return null;
        }
    };

    // #region deleteWall
    const deleteWall = async (projectId: number, wallId: number): Promise<boolean> => {
        try {
            await Axios.delete(`${environment.interiorProjectsUrl}/${projectId}/walls/${wallId}`, { headers });
            await refreshDetail(projectId);
            return true;
        } catch (err: any) {
            handleMutationError(err, projectId, 'deleting the wall');
            return false;
        }
    };

    // #region uploadWallPhoto
    const uploadWallPhoto = async (projectId: number, wallId: number, base64Image: string): Promise<InteriorWall | null> => {
        try {
            const response: AxiosResponse<InteriorWall> = await Axios.post(
                `${environment.interiorProjectsUrl}/${projectId}/walls/${wallId}/wall-photo`,
                { image: base64Image },
                { headers }
            );
            await refreshDetail(projectId);
            return response.data;
        } catch (err: any) {
            handleMutationError(err, projectId, 'uploading the wall photo');
            return null;
        }
    };

    // #region removeWallPhoto
    const removeWallPhoto = async (projectId: number, wallId: number): Promise<boolean> => {
        try {
            await Axios.delete(
                `${environment.interiorProjectsUrl}/${projectId}/walls/${wallId}/wall-photo`,
                { headers }
            );
            await refreshDetail(projectId);
            return true;
        } catch (err: any) {
            handleMutationError(err, projectId, 'removing the wall photo');
            return false;
        }
    };

    // #region replaceProjectDesigns
    // Bulk replace the project-wide design shortlist (step 2 of the designer
    // flow). rank comes from array index. A 409 means the client already
    // selected a design this save would drop — returned structured so the caller
    // can name the offending walls instead of showing a bare snackbar.
    const replaceProjectDesigns = async (
        projectId: number,
        design_ids: number[]
    ): Promise<true | SaveProjectDesignsFailure> => {
        try {
            const deduped = Array.from(new Set(design_ids));
            await Axios.put(
                `${environment.interiorProjectsUrl}/${projectId}/designs`,
                { design_ids: deduped },
                { headers }
            );
            await refreshDetail(projectId);
            return true;
        } catch (err: any) {
            const status = err?.response?.status;
            const conflicts = err?.response?.data?.trace?.designs;
            if (status === 409 && Array.isArray(conflicts)) {
                // Refresh so the shortlist on screen matches what is stored.
                await refreshDetail(projectId);
                return {
                    error: true,
                    message: err?.response?.data?.message || 'Your client already selected one of these designs.',
                    conflicts: conflicts as ProjectDesignConflict[]
                };
            }
            handleMutationError(err, projectId, 'updating the project designs');
            return { error: true, message: 'Could not update the project designs.', conflicts: [] };
        }
    };

    // #region replaceRecommendedDesigns
    const replaceRecommendedDesigns = async (projectId: number, wallId: number, design_ids: number[]): Promise<boolean> => {
        try {
            // Bulk replace — rank comes from array index; dedupe defensively.
            const deduped = Array.from(new Set(design_ids));
            await Axios.put(
                `${environment.interiorProjectsUrl}/${projectId}/walls/${wallId}/recommended-designs`,
                { design_ids: deduped },
                { headers }
            );
            await refreshDetail(projectId);
            return true;
        } catch (err: any) {
            handleMutationError(err, projectId, 'updating recommended designs');
            return false;
        }
    };

    // #region regenerateMockup
    // Re-queue a recommendation's AI mockup — backend flips it back to PENDING
    // and regenerates. Refreshes the detail so the new status/URL shows.
    const regenerateMockup = async (
        projectId: number,
        wallId: number,
        recommendedDesignId: number
    ): Promise<boolean> => {
        try {
            await Axios.post(
                `${environment.interiorProjectsUrl}/${projectId}/walls/${wallId}/recommended-designs/${recommendedDesignId}/regenerate`,
                {},
                { headers }
            );
            await refreshDetail(projectId);
            return true;
        } catch (err: any) {
            handleMutationError(err, projectId, 'regenerating the mockup');
            return false;
        }
    };

    // #region confirmProject
    // Confirm the project into a Wallmates-native order. On success refreshes the
    // detail (project flips to ORDERED). On 400 returns a structured failure with
    // the per-wall readiness breakdown (trace.walls[]) for the modal to render;
    // 409 (already ordered) / 404 refresh the detail and surface a message.
    const confirmProject = async (
        projectId: number,
        payload: ConfirmOrderPayload
    ): Promise<ConfirmOrderResult | ConfirmOrderFailure> => {
        try {
            const response: AxiosResponse<{ order_number: number }> = await Axios.post(
                `${environment.interiorProjectsUrl}/${projectId}/confirm`,
                payload,
                { headers }
            );
            await refreshDetail(projectId);
            return { order_number: response.data.order_number };
        } catch (err: any) {
            const status = err?.response?.status;
            const data = err?.response?.data;

            if (status === 409) {
                showError('This project has already been ordered. Refreshing.');
                await refreshDetail(projectId);
                return { error: true, message: data?.message ?? 'Project has already been ordered.' };
            }

            if (status === 400) {
                const wallIssues = data?.trace?.walls;
                return {
                    error: true,
                    message: data?.message ?? 'The project could not be confirmed.',
                    wallIssues: Array.isArray(wallIssues) ? wallIssues : undefined
                };
            }

            if (status === 404) {
                showError('That project no longer exists. Refreshing.');
                await refreshDetail(projectId);
                return { error: true, message: 'Project not found.' };
            }

            errorCatcher(err, loginFunctions.logout);
            return { error: true, message: 'There was an error while confirming the order. Please try again later.' };
        }
    };

    // #region shareProject
    // Generates + emails a magic link, flips the project to SHARED, and returns
    // the share_url so the designer can copy the link directly. Re-sharing
    // rotates the token (old link dies).
    const shareProject = async (
        projectId: number,
        client_email: string
    ): Promise<ShareProjectResult | null> => {
        try {
            const response: AxiosResponse<ShareProjectResult> = await Axios.post(
                `${environment.interiorProjectsUrl}/${projectId}/share`,
                { client_email },
                { headers }
            );
            await refreshDetail(projectId);
            return response.data;
        } catch (err: any) {
            handleMutationError(err, projectId, 'sharing the project');
            return null;
        }
    };

    // #region getOrderableOptions
    // Fetch the material ids the native order flow accepts so the wall editor can
    // constrain its inputs (prevents un-orderable walls at source).
    const getOrderableOptions = async (): Promise<OrderableOptions | null> => {
        try {
            const response: AxiosResponse<OrderableOptions> = await Axios.get(
                `${environment.interiorProjectsUrl}/orderable-options`,
                { headers }
            );
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    };

    // #region getOrderPreview
    // Read-only estimate of what the designer's brand will be charged if they
    // confirm (interior_designer_price × total sqft). null when it can't be
    // computed (e.g. no rate configured / walls not order-ready).
    const getOrderPreview = async (projectId: number): Promise<OrderChargePreview | null> => {
        try {
            const response: AxiosResponse<OrderChargePreview> = await Axios.get(
                `${environment.interiorProjectsUrl}/${projectId}/order-preview`,
                { headers }
            );
            return response.data;
        } catch {
            // Non-fatal — the confirm flow just omits the amount.
            return null;
        }
    };

    return [
        projects,
        projectDetail,
        {
            listProjects,
            createProject,
            getProjectDetail,
            updateProject,
            deleteProject,
            createWall,
            updateWall,
            deleteWall,
            uploadWallPhoto,
            removeWallPhoto,
            replaceProjectDesigns,
            replaceRecommendedDesigns,
            regenerateMockup,
            confirmProject,
            shareProject,
            getOrderableOptions,
            getOrderPreview
        }
    ];
};
